import {dynamic} from '@github-ui/dynamic'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useNavigableRef} from '@github-ui/navigable-ref'
import {useCallback, useEffect, useState} from 'react'

import {GlobalNavUserMenuToggle} from '../user-menu/GlobalNavUserMenuToggle'
import type {GlobalNavUserMenuProps} from '../user-menu/types'
import {useDeferredPayloadSlice} from '../utils/DeferredNavPayloadContext'

type LazyAnchoredGlobalNavUserMenuProps = Pick<GlobalNavUserMenuProps, 'owner'>

export function LazyAnchoredGlobalNavUserMenu({owner}: LazyAnchoredGlobalNavUserMenuProps) {
  const [open, setOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [hasIntent, setHasIntent] = useState(false)
  const anchorRef = useNavigableRef<HTMLButtonElement>()
  const {data: globalUserMenuProps, isPending} = useDeferredPayloadSlice('user_menu')

  useEffect(() => {
    // Prevents calling setIsLoading after the component unmounts (e.g. during
    // fast navigations), which would trigger a React state-update-on-unmounted
    // component warning.
    let cancelled = false

    async function preload() {
      try {
        await LazyGlobalNavUserMenu.preload()
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }

    // Fire-and-forget: the void operator signals to the linter that we
    // intentionally don't await this promise inside useEffect (which can't
    // return a Promise). Cleanup is handled via the `cancelled` flag instead.
    void preload()

    return () => {
      cancelled = true
    }
  }, [])

  const onClose = useCallback(() => {
    setOpen(false)
  }, [setOpen])

  const onIntent = useCallback(() => {
    setHasIntent(true)
  }, [])

  const fetchOnIntent = isFeatureEnabled('global_nav_user_menu_fetch_on_intent')

  const showLoadingState = isLoading || isPending
  const openMenu = open && !showLoadingState

  return (
    <>
      <GlobalNavUserMenuToggle
        owner={owner}
        ref={anchorRef}
        open={open}
        setOpen={setOpen}
        isLoading={open && showLoadingState}
        onIntent={fetchOnIntent ? onIntent : undefined}
      />
      {globalUserMenuProps && (
        <LazyGlobalNavUserMenu
          {...globalUserMenuProps}
          owner={owner}
          anchorRef={anchorRef}
          open={openMenu}
          onClose={onClose}
          fetchMenuData={!fetchOnIntent || hasIntent}
        />
      )}
    </>
  )
}

const LazyGlobalNavUserMenu = dynamic(
  async () => {
    const mod = await import('../user-menu/GlobalNavUserMenu')
    return {default: mod.GlobalNavUserMenu}
  },
  {ssr: false},
)
